import { Box } from '@warp-ds/react/components/box';
import { Pagination } from '@warp-ds/react/components/pagination';
import { useEffect } from 'react';

import { RangeSliderFilter } from 'shared/components/slider/RangeSliderFilter';
import { useConfig } from 'shared/config/ConfigContext';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { ResultPageLayout } from 'shared/page/result/ResultPageLayout';
import { Sorting } from 'shared/page/result/Sorting';
import { EmptyWarning } from '@/components/emptyWarning/EmptyWarning';
import { FilterGroup } from '@/components/filters/FilterGroup';
import { PriceRangeFilter } from '@/components/filters/PriceRangeFilter';
import { formatDestination } from '@/lib/utils/destination';
import { trackLmsResultPage } from '@/lib/utils/tracking';
import displayValues from '@/resources/displayValues.json';
import { LmsDateFilter } from './filters/LmsDateFilter';
import { LmsDestinationFilter } from './filters/LmsDestinationFilter';
import { LmsOriginFilter } from './filters/LmsOriginFilter';
import { LmsRatingFilter } from './filters/LmsRatingFilter';
import { LmsSupplierFilter } from './filters/LmsSupplierFilter';
import { LmsTravelTypeFilter } from './filters/LmsTravelTypeFilter';
import { useLmsDestinationFromQuery } from './hooks/useDestinationFromQuery';
import { useLmsData } from './hooks/useLmsData';
import { useLmsFilters } from './hooks/useLmsFilters';
import { LmsResultListItem } from './LmsResultListItem';
import { LmsSaveSearch } from './LmsSaveSearch';

type LmsResultPageLayoutProps = {
    isLoggedIn?: boolean;
};

const sorting: Record<string, string> = displayValues.sorting;

export const LmsResultPage = ({ isLoggedIn }: LmsResultPageLayoutProps) => {
    const { contextPathLMS } = useConfig();
    const { data, error, isLoading } = useLmsData();
    const { filters, updateFilters, resetFilters } = useLmsFilters();
    const destinationQuery = useLmsDestinationFromQuery();
    const formattedDestination = formatDestination(destinationQuery);

    const sortOptions = data?.sortDropdownKeys?.map((option) => ({ label: sorting[option], value: option })) ?? [];

    // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger tracking when the filters change.
    useEffect(() => {
        trackLmsResultPage(filters, data?.numFound);
    }, [filters]); // Only trigger tracking when the filters change. cant do it on offers because of swr will load data on page load

    const handlePageChange = (page: number) => {
        updateFilters('pageNumber', page === 1 ? undefined : page);
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    };

    const handleSortChange = (newSort?: string) => {
        updateFilters('sorter', newSort);
    };

    const renderFilters = () => (
        <>
            <FilterGroup title="Avreisested" testId="originFilter">
                <LmsOriginFilter data={data?.airportFacet} filters={filters} updateFilters={updateFilters} />
            </FilterGroup>
            <div data-testid="departureDateFilter" className="p-16 space-y-16">
                <h3 className="h4">Dato</h3>
                <LmsDateFilter filters={filters} updateFilters={updateFilters} />
                <div className="text-xs s-text-subtle">Restplasser finnes kun for reiser nært frem i tid</div>
            </div>
            <FilterGroup title="Reiselengde" testId="durationFilter">
                <RangeSliderFilter
                    id="durationRange"
                    title="Antall dager"
                    label={['Fra dager', 'Til dager']}
                    value={[filters.lengde?.from ?? 1, filters.lengde?.to ?? 30]}
                    min={1}
                    max={30}
                    renderValue={(val) => `${val} ${val === 1 ? 'dag' : 'dager'}`}
                    onChange={(value) => updateFilters('lengde', { from: value[0], to: value[1] })}
                    disabled={!data}
                />
            </FilterGroup>
            <FilterGroup title="Reisetype" testId="travelTypeFilter">
                <LmsTravelTypeFilter data={data?.typeFacet} filters={filters} updateFilters={updateFilters} />
            </FilterGroup>
            <FilterGroup title="Reisemål" testId="destinationFilter" className="space-y-16">
                <LmsDestinationFilter data={data?.locationFacet} filters={filters} updateFilters={updateFilters} />
            </FilterGroup>
            <FilterGroup title="Pris per person" testId="priceFilter">
                <PriceRangeFilter
                    id="priceRange"
                    legend="Pris per person"
                    labels={['Fra kroner', 'Til kroner']}
                    affix="kr"
                    range={[filters.pris?.from ?? null, filters.pris?.to ?? null]}
                    updateRange={(values) => updateFilters('pris', values ? { from: values[0], to: values[1] } : undefined)}
                />
            </FilterGroup>
            <FilterGroup title="Hotellstandard" testId="ratingFilter">
                <LmsRatingFilter filters={filters} updateFilters={updateFilters} />
                <div className="text-xs s-text-subtle mt-12">Gjelder kun reiser med spesifisert bosted</div>
            </FilterGroup>
            <FilterGroup title="Tilbydere og selskaper" testId="supplierFilter" className="space-y-16">
                <LmsSupplierFilter data={data?.supplierFacet} filters={filters} updateFilters={updateFilters} />
            </FilterGroup>
        </>
    );

    return (
        <>
            <SimpleHead
                title={`Restplasser - ${formattedDestination ?? 'sammenlign pris'} | FINN reise`}
                description="Finn billige restplasser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
                canonical={`https://www.finn.no/reise/restplasser${destinationQuery ? `?til=${destinationQuery}` : ''}`}
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/restplasser">
                <ResultPageLayout
                    title="Restplasser"
                    breadcrumbs={{ path: 'restplasser' }}
                    filters={renderFilters()}
                    sortingSection="pakkereiser"
                    sorting={
                        <Sorting
                            sortOptions={sortOptions}
                            defaultSort="pris_lms"
                            selectedSort={filters.sorter}
                            onChange={handleSortChange}
                        />
                    }
                    saveSearch={<LmsSaveSearch filters={filters} isLoggedIn={isLoggedIn} />}>
                    <Box as="section" info className="flex gap-16 mb-16 items-center">
                        <w-icon name="Info" size="20px" locale="nb" className="s-text-link" aria-hidden></w-icon>
                        <div className="flex-1 text-s" data-testid="disclaimer">
                            Prisene som oppgis er <strong>pris per person</strong> når to voksne reiser sammen og bor på samme rom i
                            billigste boalternativ. Husk at reiser kan ha endret pris eller blitt utsolgt innen du fullfører bestillingen.
                        </div>
                    </Box>
                    <section id="resultList" data-testid="resultList" className="relative space-y-16">
                        {error && (
                            <Box className="flex gap-16 s-bg-negative-subtle items-center">
                                <w-icon name="Error" size="20px" locale="nb" className="s-text-negative" aria-hidden></w-icon>
                                <span data-testid="resultsError">
                                    Ooops... En feil oppsto ved henting av nye tilbud, vennligst prøv igjen.
                                </span>
                            </Box>
                        )}
                        {data?.offers?.length ? null : <EmptyWarning onReset={resetFilters} testId="emptyWarning-results" />}
                        {data?.offers?.map((offer) => (
                            <LmsResultListItem key={offer.offerId} offer={offer} isLoading={isLoading} />
                        ))}
                    </section>
                    {data?.currentPage && data?.totalPages > 1 ? (
                        <Pagination
                            createHref={(p: number) => `${contextPathLMS}?pageNumber=${p}`}
                            currentPage={data.currentPage}
                            lastPage={data.totalPages}
                            onChange={handlePageChange}
                        />
                    ) : null}
                </ResultPageLayout>
            </Main>
        </>
    );
};
